/**
 * 케어 코치(챗봇) API — BE 계약: `/v1/health/ctbt/*`
 *
 * BE 구현(common-service)은 현재 **임시 목 데이터**를 반환한다(DB/외부 AI 없음).
 * 추후 실제 AI 연동 시 BE 내부만 교체되며 본 계약(요청/응답 형태)은 유지된다.
 *
 * 엔드포인트(6):
 *   POST /v1/health/ctbt/message       사용자 메시지 → 인텐트 매칭 응답
 *   GET  /v1/health/ctbt/suggested     추천 질문 chips
 *   POST /v1/health/ctbt/photo         사진 첨부 → 음식 인식 응답
 *   POST /v1/health/ctbt/dinner        '저녁 메뉴 추천' 후속
 *   GET  /v1/health/ctbt/new-message   홈 '새 메시지' 알림 표시 여부 + 카운트
 *   GET  /v1/health/ctbt/dgns/latest   코치 진단 콘텐츠(단계전환 + 5섹션)
 */

import { TApiResponseData } from '../apiTypes';
import instance from '../axios';

const CTBT_URL = '/v1/health/ctbt';

/** 발신 유형 코드 (SNDG_TYPE_CD) */
export const CTBT_SNDG_TYPE = { USER: 730001, AI: 730002 } as const;

// ==================== 공통 카드 타입 (BE CtbtCard) ====================

/** 카드 액션 버튼. action/topic 은 클라이언트 네비게이션용 문자열(예: goToCare, showRoutineAnalysis, save-meal). */
export interface CtbtCardAction {
  label: string;
  action?: string | null;
  topic?: string | null;
  primary?: boolean;
}

/** 인사이트/코칭 카드 */
export interface CtbtCard {
  header: string;
  title: string;
  desc: string; // <strong> / <br> HTML 허용
  actions?: CtbtCardAction[];
}

// ==================== 1) 대화 메시지 ====================

export interface CtbtMessageResponse {
  /** AI 자연어 응답(다중 — 클라이언트가 순차 표시) */
  messages: string[];
  /** 인사이트 카드 (optional) */
  card?: CtbtCard | null;
}

/** 사용자 메시지 전송 → 인텐트 매칭 목 응답 */
export const sendChatbotMessage = async (msgCntnt: string) => {
  try {
    const url = `${CTBT_URL}/message`;
    const response = await instance.post<TApiResponseData<CtbtMessageResponse>>(url, {
      sndgTypeCd: CTBT_SNDG_TYPE.USER,
      msgCntnt,
    });
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

// ==================== 2) 추천 질문 chips ====================

export interface CtbtSuggestedResponse {
  /** 추천 질문 문자열 목록(라벨=프롬프트) */
  suggestions: string[];
}

/** 추천 질문 chips 조회 (정적 6종) */
export const getSuggested = async () => {
  try {
    const url = `${CTBT_URL}/suggested`;
    const response = await instance.get<TApiResponseData<CtbtSuggestedResponse>>(url);
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

// ==================== 3) 사진 첨부 분석 ====================

/** 음식 영양 카드 (수치는 표시용 문자열) */
export interface CtbtFoodCard {
  emoji: string;
  name: string;
  portion: string;
  kcal: string;
  protein: string;
  carbs: string;
  sodium: string;
  warning: string; // <strong> / <br> HTML 허용
}

export interface CtbtPhotoResponse {
  messages: string[];
  foodCard: CtbtFoodCard;
  coachCard: CtbtCard;
}

/** 사진 첨부 메시지 → 음식 인식 목 응답(짜장면↔비빔밥 2종 순환) */
export const sendChatbotPhoto = async (fileUuid?: string) => {
  try {
    const url = `${CTBT_URL}/photo`;
    const response = await instance.post<TApiResponseData<CtbtPhotoResponse>>(url, { fileUuid });
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

// ==================== 4) 저녁 메뉴 추천 후속 ====================

/** 음식 코칭 카드 'recommend-next' 후속. warn=직전 음식이 경고 메뉴(짜장면 등)였는지. */
export const recommendDinner = async (warn: boolean) => {
  try {
    const url = `${CTBT_URL}/dinner`;
    const response = await instance.post<TApiResponseData<CtbtMessageResponse>>(url, { warn });
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

// ==================== 5) 홈 '새 메시지' 알림 ====================

export interface CtbtNewMessageResponse {
  /** 알림 노출 여부 */
  show: boolean;
  badge?: string | null;
  time?: string | null;
  /** 미확인 메시지 수(단계전환+루틴진단 → 등록 사용자 2) */
  count?: number | null;
  icon?: string | null;
  title?: string | null;
  /** 클릭 시 동작(예: showRoutineAnalysis) */
  action?: string | null;
}

/**
 * 홈 '새 메시지' 알림 표시 여부 조회.
 * 24h 숨김은 서버 무상태 — 클라이언트가 마지막 확인 시각(epochMillis)을 보관 후 전달한다.
 */
export const getNewMessage = async (lastCheckedAt?: number) => {
  try {
    const url = `${CTBT_URL}/new-message`;
    const response = await instance.get<TApiResponseData<CtbtNewMessageResponse>>(url, {
      params: lastCheckedAt != null ? { lastCheckedAt } : undefined,
    });
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

// ==================== 6) 코치 진단 콘텐츠 ====================

export interface CtbtDgnsStageCard {
  icon: string;
  title: string;
  body: string;
  primaryAction: { label: string; action: string };
  doneText: string;
}

export interface CtbtDgnsSection {
  icon: string;
  title: string;
  body: string;
}

export interface CtbtDgnsRecommend {
  label: string;
  tag: string;
  tagStyle?: string | null;
  items: string[];
}

export interface CtbtDgnsAction {
  label: string;
  secondary?: boolean;
}

export interface CtbtDgnsResponse {
  stageCard: CtbtDgnsStageCard;
  summary: CtbtDgnsSection;
  goodList: string[];
  warnList: string[];
  recommends: CtbtDgnsRecommend[];
  question: string;
  actions: CtbtDgnsAction[];
}

/** 최신 코치 진단 1건(목 고정) — '새 메시지' 클릭 시 표시 */
export const getDgnsLatest = async () => {
  try {
    const url = `${CTBT_URL}/dgns/latest`;
    const response = await instance.get<TApiResponseData<CtbtDgnsResponse>>(url);
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};
